import React, { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { ClubCard, ClubCardSkeleton } from '@/components/clubs/ClubCard';
import { ClubFilters } from '@/components/clubs/ClubFilters';
import { Button } from '@/components/ui/button';
import { Trophy, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  fetchClubs,
  fetchSports,
  fetchRegionCountries,
  fetchRegionSubregions,
  fetchRegionProvinces,
  fetchRegionCities,
  friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';
import { CLUBS as TID } from '@/constants/testIds';

const INITIAL_FILTERS = {
  sport: '',
  country: '',
  region: '',
  province: '',
  city: '',
};

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function ClubsList() {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query, 350);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [sports, setSports] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);

  const [data, setData] = useState({ items: [], total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);

  // Cargar deportes + países iniciales
  useEffect(() => {
    (async () => {
      try {
        const [sportsRes, countriesRes] = await Promise.all([
          fetchSports(),
          fetchRegionCountries(),
        ]);
        setSports(sportsRes);
        setCountries(countriesRes);
      } catch (err) {
        toast.error(friendlyError(err));
      }
    })();
  }, []);

  // Cuando cambia country → cargar regions; resetear region/province/city si no aplica
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchRegionSubregions(filters.country || undefined);
        setRegions(res);
      } catch {
        setRegions([]);
      }
    })();
  }, [filters.country]);

  // Cuando cambia country/region → cargar provinces
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchRegionProvinces({
          country: filters.country || undefined,
          region: filters.region || undefined,
        });
        setProvinces(res);
      } catch {
        setProvinces([]);
      }
    })();
  }, [filters.country, filters.region]);

  // Cuando cambia country/region/province → cargar cities
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchRegionCities({
          country: filters.country || undefined,
          region: filters.region || undefined,
          province: filters.province || undefined,
        });
        setCities(res);
      } catch {
        setCities([]);
      }
    })();
  }, [filters.country, filters.region, filters.province]);

  // Cargar clubes ante cambios de filtros/búsqueda/página
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchClubs({
          q: debouncedQuery || undefined,
          sport: filters.sport || undefined,
          country: filters.country || undefined,
          region: filters.region || undefined,
          province: filters.province || undefined,
          city: filters.city || undefined,
          page,
          page_size: pageSize,
        });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) toast.error(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, filters, page]);

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // Resetear niveles inferiores al cambiar uno superior
      if (key === 'country') {
        next.region = '';
        next.province = '';
        next.city = '';
      } else if (key === 'region') {
        next.province = '';
        next.city = '';
      } else if (key === 'province') {
        next.city = '';
      }
      return next;
    });
    setPage(1);
  };

  const clearAll = () => {
    setQuery('');
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const isEmpty = !loading && data.items.length === 0;
  const totalPages = data.pages || 0;

  const resultsLabel = useMemo(() => {
    if (loading) return 'Cargando clubes…';
    if (data.total === 0) return 'Sin resultados';
    if (data.total === 1) return '1 club encontrado';
    return `${data.total} clubes encontrados`;
  }, [loading, data.total]);

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid={TID.page}>
        <header className="mb-8 sm:mb-10">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">
            Canales OTT
          </p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-[var(--ns-text)] leading-tight">
            Descubre los clubes
          </h1>
          <p className="mt-3 text-[var(--ns-text-muted)] max-w-2xl text-sm sm:text-base leading-relaxed">
            Explora los clubes y canales OTT disponibles. Filtra por deporte y
            por territorio para encontrar el contenido que más te interesa.
          </p>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-8">
          {/* Filtros */}
          <aside className="sm:col-span-4 lg:col-span-3">
            <ClubFilters
              query={query}
              setQuery={setQuery}
              filters={filters}
              setFilter={setFilter}
              clearAll={clearAll}
              sports={sports}
              countries={countries}
              regions={regions}
              provinces={provinces}
              cities={cities}
            />
          </aside>

          {/* Resultados */}
          <section className="sm:col-span-8 lg:col-span-9">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[var(--ns-text-muted)]">{resultsLabel}</span>
              {totalPages > 1 ? (
                <span className="text-xs text-[var(--ns-text-subtle)]">
                  Página {data.page || page} de {totalPages}
                </span>
              ) : null}
            </div>

            {loading ? (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                data-testid={TID.loadingSkeleton}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <ClubCardSkeleton key={i} />
                ))}
              </div>
            ) : isEmpty ? (
              <div
                className="rounded-2xl border border-dashed border-[var(--ns-border)] bg-white p-10 text-center"
                data-testid={TID.emptyState}
              >
                <div className="h-14 w-14 mx-auto rounded-2xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] inline-flex items-center justify-center mb-4">
                  {query ? <Search className="h-6 w-6" /> : <Trophy className="h-6 w-6" />}
                </div>
                <h3 className="font-display text-lg text-[var(--ns-text)]">
                  No hemos encontrado clubes
                </h3>
                <p className="mt-1 text-sm text-[var(--ns-text-muted)] max-w-md mx-auto">
                  Prueba a quitar algún filtro o cambiar la búsqueda. Más clubes
                  llegarán pronto a NetScouters.
                </p>
                <Button
                  className="mt-5 h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0 hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white transition-all"
                  onClick={clearAll}
                >
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
                data-testid={TID.grid}
              >
                {data.items.map((club) => (
                  <ClubCard key={club.id} club={club} />
                ))}
              </div>
            )}

            {/* Paginación simple */}
            {totalPages > 1 ? (
              <div
                className="mt-8 flex items-center justify-between gap-4"
                data-testid={TID.pagination}
              >
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-xl border-[var(--ns-border)] disabled:opacity-50"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-xl border-[var(--ns-border)] disabled:opacity-50"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Siguiente <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
